import { z } from 'zod';

// Payment method type enum
export const PaymentMethodTypeEnum = {
  CARD: 'card',
  US_BANK_ACCOUNT: 'us_bank_account',
} as const;

export type PaymentMethodType = (typeof PaymentMethodTypeEnum)[keyof typeof PaymentMethodTypeEnum];

// Payment method status enum
export const PaymentMethodStatusEnum = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  FAILED: 'failed',
  DELETED: 'deleted',
} as const;

export type PaymentMethodStatus =
  (typeof PaymentMethodStatusEnum)[keyof typeof PaymentMethodStatusEnum];

// Legacy payment method - stored on connected account (org-specific)
export const LegacyPaymentMethodSchema = z.object({
  connectedAccountId: z.string().min(1, 'Connected Account ID is required'),
  stripeCustomerId: z.string().min(1, 'Stripe Customer ID is required'),
  stripePaymentMethodId: z.string().min(1, 'Stripe Payment Method ID is required'),
});

// Platform payment method - stored on platform account (works across all orgs)
export const PlatformPaymentMethodSchema = z.object({
  stripeCustomerId: z.string().min(1, 'Platform Stripe Customer ID is required'),
  stripePaymentMethodId: z.string().min(1, 'Platform Stripe Payment Method ID is required'),
});

// Base saved payment method schema with discriminated union
// A payment method has either `legacy` OR `platform`, never both
export const SavedPaymentMethodSchema = z
  .object({
    _id: z.string().optional(),
    customerId: z.string().min(1, 'Customer ID is required'),

    // Discriminated union - ONE of these, never both
    legacy: LegacyPaymentMethodSchema.optional(),
    platform: PlatformPaymentMethodSchema.optional(),

    type: z.enum([PaymentMethodTypeEnum.CARD, PaymentMethodTypeEnum.US_BANK_ACCOUNT]),

    // Display info (safe to store - no sensitive data)
    lastFour: z.string().length(4, 'Last four digits must be exactly 4 characters'),
    brand: z.string().optional(), // For cards: visa, mastercard, amex, discover, etc.
    bankName: z.string().optional(), // For ACH/bank accounts
    expirationMonth: z.number().int().min(1).max(12).optional(), // For cards
    expirationYear: z.number().int().min(2024).optional(), // For cards
    billingZip: z.string().optional(),

    isPrimary: z.boolean().default(false),
    status: z
      .enum([
        PaymentMethodStatusEnum.ACTIVE,
        PaymentMethodStatusEnum.EXPIRED,
        PaymentMethodStatusEnum.FAILED,
        PaymentMethodStatusEnum.DELETED,
      ])
      .default(PaymentMethodStatusEnum.ACTIVE),
    lastUsedAt: z.date().optional(),
    deletedAt: z.date().optional(),

    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
  })
  .refine((data) => (data.legacy && !data.platform) || (!data.legacy && data.platform), {
    message: 'Payment method must have either legacy or platform, but not both',
  });

// Schema for creating a saved payment method (platform - new PMs always go to platform)
export const SavedPaymentMethodCreateSchema = z.object({
  customerId: z.string().min(1, 'Customer ID is required'),
  platform: PlatformPaymentMethodSchema,
  type: z.enum([PaymentMethodTypeEnum.CARD, PaymentMethodTypeEnum.US_BANK_ACCOUNT]),

  lastFour: z.string().length(4, 'Last four digits must be exactly 4 characters'),
  brand: z.string().optional(),
  bankName: z.string().optional(),
  expirationMonth: z.number().int().min(1).max(12).optional(),
  expirationYear: z.number().int().min(2024).optional(),
  billingZip: z.string().optional(),

  isPrimary: z.boolean().optional().default(false),
});

// Schema for creating a legacy payment method (for migration only)
export const SavedPaymentMethodCreateLegacySchema = z.object({
  customerId: z.string().min(1, 'Customer ID is required'),
  legacy: LegacyPaymentMethodSchema,
  type: z.enum([PaymentMethodTypeEnum.CARD, PaymentMethodTypeEnum.US_BANK_ACCOUNT]),

  lastFour: z.string().length(4, 'Last four digits must be exactly 4 characters'),
  brand: z.string().optional(),
  bankName: z.string().optional(),
  expirationMonth: z.number().int().min(1).max(12).optional(),
  expirationYear: z.number().int().min(2024).optional(),
  billingZip: z.string().optional(),

  isPrimary: z.boolean().optional().default(false),
  status: z
    .enum([
      PaymentMethodStatusEnum.ACTIVE,
      PaymentMethodStatusEnum.EXPIRED,
      PaymentMethodStatusEnum.FAILED,
      PaymentMethodStatusEnum.DELETED,
    ])
    .optional(),
  lastUsedAt: z.date().optional(),
});

// Schema for updating a saved payment method
export const SavedPaymentMethodUpdateSchema = z.object({
  _id: z.string().min(1, 'Payment method ID is required'),
  isPrimary: z.boolean().optional(),
  status: z
    .enum([
      PaymentMethodStatusEnum.ACTIVE,
      PaymentMethodStatusEnum.EXPIRED,
      PaymentMethodStatusEnum.FAILED,
      PaymentMethodStatusEnum.DELETED,
    ])
    .optional(),
  lastUsedAt: z.date().optional(),
  deletedAt: z.date().optional(),
  // Card expiration can be updated when Stripe notifies us
  expirationMonth: z.number().int().min(1).max(12).optional(),
  expirationYear: z.number().int().min(2024).optional(),
});

// Schema for listing saved payment methods (no org required)
export const SavedPaymentMethodListSchema = z.object({
  customerId: z.string().min(1, 'Customer ID is required'),
  includeDeleted: z.boolean().optional().default(false),
});

// Schema for getting a single saved payment method (no org required)
export const SavedPaymentMethodGetByIdSchema = z.object({
  _id: z.string().min(1, 'Payment method ID is required'),
  customerId: z.string().min(1, 'Customer ID is required'),
});

// Schema for deleting a saved payment method (no org required)
export const SavedPaymentMethodDeleteSchema = z.object({
  _id: z.string().min(1, 'Payment method ID is required'),
  customerId: z.string().min(1, 'Customer ID is required'),
});

// Schema for setting primary payment method (no org required)
export const SavedPaymentMethodSetPrimarySchema = z.object({
  _id: z.string().min(1, 'Payment method ID is required'),
  customerId: z.string().min(1, 'Customer ID is required'),
});

// Schema for checking deletion eligibility (no org required)
export const SavedPaymentMethodCheckDeletionSchema = z.object({
  _id: z.string().min(1, 'Payment method ID is required'),
  customerId: z.string().min(1, 'Customer ID is required'),
});

// TypeScript types derived from schemas
export type LegacyPaymentMethod = z.infer<typeof LegacyPaymentMethodSchema>;
export type PlatformPaymentMethod = z.infer<typeof PlatformPaymentMethodSchema>;
export type SavedPaymentMethod = z.infer<typeof SavedPaymentMethodSchema>;
export type SavedPaymentMethodCreate = z.infer<typeof SavedPaymentMethodCreateSchema>;
export type SavedPaymentMethodCreateLegacy = z.infer<typeof SavedPaymentMethodCreateLegacySchema>;
export type SavedPaymentMethodUpdate = z.infer<typeof SavedPaymentMethodUpdateSchema>;
export type SavedPaymentMethodList = z.infer<typeof SavedPaymentMethodListSchema>;
export type SavedPaymentMethodGetById = z.infer<typeof SavedPaymentMethodGetByIdSchema>;
export type SavedPaymentMethodDelete = z.infer<typeof SavedPaymentMethodDeleteSchema>;
export type SavedPaymentMethodSetPrimary = z.infer<typeof SavedPaymentMethodSetPrimarySchema>;
export type SavedPaymentMethodCheckDeletion = z.infer<typeof SavedPaymentMethodCheckDeletionSchema>;

// Response type for deletion eligibility check
export interface DeletionEligibilityResult {
  canDelete: boolean;
  blockedByOrders?: string[]; // Order numbers that would be affected
  message?: string;
}

// Helper type to extract stripe IDs from a saved payment method
export interface SavedPaymentMethodStripeIds {
  stripeCustomerId: string;
  stripePaymentMethodId: string;
  connectedAccountId?: string;
  isPlatform: boolean;
}

// Input type for getStripeIds - accepts both schema and model types
export interface SavedPaymentMethodLike {
  legacy?: LegacyPaymentMethod;
  platform?: PlatformPaymentMethod;
}

// Helper function to extract stripe IDs from a saved payment method
// Accepts both schema-based and model-based SavedPaymentMethod types
export const getStripeIds = (pm: SavedPaymentMethodLike): SavedPaymentMethodStripeIds => {
  if (pm.legacy) {
    return {
      stripeCustomerId: pm.legacy.stripeCustomerId,
      stripePaymentMethodId: pm.legacy.stripePaymentMethodId,
      connectedAccountId: pm.legacy.connectedAccountId,
      isPlatform: false,
    };
  }
  if (pm.platform) {
    return {
      stripeCustomerId: pm.platform.stripeCustomerId,
      stripePaymentMethodId: pm.platform.stripePaymentMethodId,
      connectedAccountId: undefined,
      isPlatform: true,
    };
  }
  throw new Error('Invalid saved payment method: missing both legacy and platform');
};
